import * as React from 'react';
import { Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { Metadata, SchemaField } from '../../../types';
import { useSelector } from '../../../utils/useSelector';
import { useActions } from '../metadataFormActions';
import AccessControl from '../../AccessControl/accessControl';
import { Resource } from '../../../enums/Resource';
import { ApprovalType } from '../../../enums/ApprovalType';
import { Dropbox } from '../../Dropbox/dropbox';
import { ApprovalsMenu } from './approvalsMenu';
import { DownArrow } from '../../Icon/icon';
import {
  canActionTask,
  getDataValues,
  getInheritedContentTypeConfiguration,
  getItemContentTypeId,
  getItemType,
  openConfigureWorkflowModal
} from '../../../utils';
import { ItemType } from '../../../enums/ItemType';
import { ItemStatus } from '../../../enums/ItemStatus';
import { ReviewDone } from '../../../enums/ReviewDone';
import { Role } from '../../../enums/Role';
import { FormUpdater } from '../../../types/Field';
import { useIsAuthorPromotable } from '../../../utils/useIsAuthorPromotable';

interface ApprovalsProps {
  data?: Partial<Metadata>;
  fields?: SchemaField[];
  updater?: FormUpdater;
  updatingStatus: boolean;
  pushToCPP: () => any;
  disabled: boolean;
}

export const Approvals = ({
  updater,
  updatingStatus,
  pushToCPP,
  disabled
}: ApprovalsProps) => {
  const data = useSelector(s => s.form?.data);
  const actionEmployeeId = useSelector(
    s => s.view?.actingAs?.props?.empCd || s.app?.user?.employeeId
  );
  const backingUpFor = useSelector(s => s.view?.backingUpFor) || null;
  const pushResponse = useSelector(s => s.form?.pushResponse);
  const approvalInProgress = useSelector(s => s.form?.approvalInProgress);
  const { doAction, setShowConfigureWorkflowModal } = useActions();
  const validateInProgressMsg = useSelector(s => s.form?.validateInProgressMsg);
  const [approveDisabled, setApprovedDisabled] = React.useState(false);
  const [rejectDisabled, setRejectDisabled] = React.useState(false);
  const adminApproval =
    useSelector(s =>
      s.view?.actingAs != null
        ? false
        : s.app?.user?.roles?.includes(Role.admin)
    ) &&
    process.env.ENVIRONMENT != 'PROD' &&
    process.env.ENVIRONMENT != 'DEVB';
  const contentTypeId = getItemContentTypeId(data);
  const { researchModelId, subtypeId } = getDataValues(data);
  const config = useSelector(s =>
    getInheritedContentTypeConfiguration(
      s.planning.contentTypeConfigurations,
      researchModelId,
      contentTypeId,
      subtypeId
    )
  );

  const creativeHelp = config?.props?.creativeHelp;

  const isAnalystTakeOrFirstTake = useIsAuthorPromotable();

  const setActionBtnDisabled = action => {
    if (approvalInProgress || validateInProgressMsg) return true;
    if (!!pushResponse && pushResponse?.errors?.length > 0) return false;
    if (isAnalystTakeOrFirstTake && !data?.approvals?.length) return false;

    let allAssignedActioned = true;

    for (const approval of data?.approvals) {
      const actionableByUser =
        isAnalystTakeOrFirstTake ||
        adminApproval ||
        canActionTask(approval, backingUpFor, actionEmployeeId);
      if (actionableByUser && approval?.approvalStatus != action) {
        allAssignedActioned = false;
      }
    }
    return allAssignedActioned;
  };

  const isTaskAssignee =
    isAnalystTakeOrFirstTake ||
    data?.approvals?.some(
      approval =>
        adminApproval || canActionTask(approval, backingUpFor, actionEmployeeId)
    );

  const itemType = getItemType(data);

  return (
    !updatingStatus && (
      <>
        {!isAnalystTakeOrFirstTake && (
          <AccessControl resource={Resource.push} data={data}>
            <Primary
              className="push-button"
              disabled={disabled}
              onClick={pushToCPP}
            >
              {itemType == ItemType.globalSubmission
                ? ReviewDone.SubmitToConf
                : ReviewDone.StartProduction}
            </Primary>
          </AccessControl>
        )}
        <div className="approvals">
          {!!data?.approvals?.length && (
            <div className="approvals-menu">
              <Dropbox
                className="filter-dropbox"
                defaultShow={false}
                disabled={false}
                position="right"
                renderBox={() => {
                  return <ApprovalsMenu approvals={data?.approvals} />;
                }}
              >
                <div className="approvals-dd">
                  Approvals
                  <DownArrow />
                </div>
              </Dropbox>
            </div>
          )}
          {isTaskAssignee && (
            <div className="approval-buttons">
              {!isAnalystTakeOrFirstTake && (
                <Secondary
                  className="reject-mq-button"
                  disabled={
                    setActionBtnDisabled(ApprovalType.Declined) || rejectDisabled
                  }
                  onClick={() => {
                    doAction(
                      ApprovalType.Declined,
                      actionEmployeeId,
                      backingUpFor,
                      adminApproval
                    );
                    setApprovedDisabled(false);
                  }}
                >
                  Decline
                </Secondary>
              )}
              <Primary
                className="approve-mq-button"
                disabled={
                  setActionBtnDisabled(ApprovalType.Approved) || approveDisabled
                }
                onClick={() => {
                  if (isAnalystTakeOrFirstTake) {
                    if (!data?.approvals?.length) {
                      pushToCPP();
                    } else {
                      doAction(
                        ApprovalType.Approved,
                        actionEmployeeId,
                        backingUpFor,
                        adminApproval || isAnalystTakeOrFirstTake
                      );
                      setRejectDisabled(false);
                    }
                  } else {
                    const shouldOpenConfigureWorkflow =
                      openConfigureWorkflowModal(
                        data,
                        backingUpFor,
                        actionEmployeeId,
                        ApprovalType.Approved,
                        adminApproval,
                        creativeHelp
                      );

                    if (shouldOpenConfigureWorkflow) {
                      setShowConfigureWorkflowModal(true);
                    } else {
                      doAction(
                        ApprovalType.Approved,
                        actionEmployeeId,
                        backingUpFor,
                        adminApproval
                      );
                      setRejectDisabled(false);
                    }
                  }
                }}
              >
                {isAnalystTakeOrFirstTake ? 'Push to Production' : 'Approve'}
              </Primary>
            </div>
          )}
        </div>
      </>
    )
  );
};
